import axios from 'axios'
import {accessSessionTokenKeyName} from 'src/utils/constants'
import {getValidAdminAccessToken} from 'src/utils/impersonationTokens'

export type ManageSettingFileAction = 'DOWNLOAD' | 'UPLOAD'

export type ManageSettingFileParams = {
  platform_id: number
  available_platform_setting_id: number
  setting_mapping_id?: number
}

const MANAGE_SETTING_FILE_ENDPOINT = '/api/manage-setting-file'

const getAuthHeaders = () => {
  const token = localStorage.getItem(accessSessionTokenKeyName)
  const logInAdminAccessToken = getValidAdminAccessToken()

  return {
    Authorization: token ? `Bearer ${token}` : '',
    ...(logInAdminAccessToken ? {xadmin: logInAdminAccessToken} : {}),
  }
}

const QBWC_XML_MARKER = '<QBWCXML>'

const sanitizeFilenamePart = (value: string) =>
  value
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_')
    .replace(/\s+/g, '_')

const extractAppNameFromQwcXml = (xml: string): string | null => {
  const match = xml.match(/<AppName>([\s\S]*?)<\/AppName>/i)
  return match?.[1]?.trim() || null
}

const extractFilename = (contentDisposition?: string, fallback = 'download') => {
  if (!contentDisposition) return fallback

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1])
    } catch {
      // fall through to filename=
    }
  }

  const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i)
  return match?.[1]?.replace(/['"]/g, '') || fallback
}

const getResponseHeader = (headers: Record<string, unknown>, name: string): string | undefined => {
  const normalized = name.toLowerCase()
  const entry = Object.entries(headers).find(([key]) => key.toLowerCase() === normalized)
  const value = entry?.[1]
  return typeof value === 'string' ? value : undefined
}

const resolveManageSettingDownloadFilename = async (
  blob: Blob,
  contentDisposition: string | undefined,
  contentType: string
): Promise<string> => {
  const headerFilename = extractFilename(contentDisposition)
  const hasTrustedFilename =
    Boolean(contentDisposition) &&
    headerFilename !== 'download' &&
    !headerFilename.endsWith('.xml')

  if (hasTrustedFilename) {
    return headerFilename
  }

  const sample = await blob.slice(0, 4096).text()
  if (!sample.includes(QBWC_XML_MARKER)) {
    return headerFilename
  }

  if (headerFilename.endsWith('.xml')) {
    return headerFilename.replace(/\.xml$/i, '.qwc')
  }

  const appName = extractAppNameFromQwcXml(sample)
  if (appName) {
    return `${sanitizeFilenamePart(appName)}.qwc`
  }

  return contentType.includes('xml') ? 'QuickBooksWebConnector.qwc' : 'download.qwc'
}

export const extractManageSettingFileErrorMessage = async (error: any): Promise<string> => {
  const data = error?.response?.data

  if (data instanceof Blob) {
    try {
      const text = await data.text()
      const json = JSON.parse(text)
      return json.message || json.detail || 'Unable to complete file request.'
    } catch {
      return 'Unable to complete file request.'
    }
  }

  return data?.message || data?.detail || error?.message || 'Unable to complete file request.'
}

export const downloadManageSettingFile = async (
  params: ManageSettingFileParams
): Promise<{filename: string}> => {
  const response = await axios({
    url: `${process.env.REACT_APP_API_URL}${MANAGE_SETTING_FILE_ENDPOINT}`,
    method: 'POST',
    params: {
      action: 'DOWNLOAD',
      ...params,
    },
    responseType: 'blob',
    headers: getAuthHeaders(),
  })

  const contentType = response.headers['content-type'] ?? ''
  const contentDisposition = getResponseHeader(
    response.headers as Record<string, unknown>,
    'content-disposition'
  )

  if (contentType.includes('application/json')) {
    const text = await (response.data as Blob).text()
    const json = JSON.parse(text)
    throw new Error(json.message || json.detail || 'Unable to download file.')
  }

  const blob = response.data as Blob
  const filename = await resolveManageSettingDownloadFilename(
    blob,
    contentDisposition,
    contentType
  )
  const blobUrl = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = blobUrl
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.URL.revokeObjectURL(blobUrl)

  return {filename}
}

export const uploadManageSettingFile = async (params: ManageSettingFileParams, file: File) => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await axios({
    url: `${process.env.REACT_APP_API_URL}${MANAGE_SETTING_FILE_ENDPOINT}`,
    method: 'POST',
    data: formData,
    params: {
      action: 'UPLOAD',
      ...params,
    },
    headers: getAuthHeaders(),
  })

  return response.data
}
